/** @jsx jsx */
import React from 'react';

import { jsx } from '@emotion/react';
import { Box, Text } from '@opendoor/bricks/core';
import Input, { InputProps } from '@opendoor/bricks/core/Input';
import { checkTelValidity } from '@opendoor/bricks/core/Input/helpers';
import Link from '@opendoor/bricks/core/Link';
import { isNil } from 'lodash';

import { NamelessAccountContext } from '../../growth/auth/NamelessAccountContext';
import { IAccountCombinedFormProps } from './AccountCombinedForm';
import AccountFormControls from './AccountFormControls';
import { Disclaimer, DisclaimerProps, Form, PHONE_DEFAULT_DISCLAIMER } from './shared';

export interface IAccountPasswordFormProps {
  /**
   * Name of the account form in analytics events, should be unique within the app
   * Convention: {appName}-{accountFormName}
   */
  analyticsName: string;
  email: string;
  name?: string;
  phone?: string;
  loading?: boolean;
  loginError?: string | boolean;
  isNewHuman: boolean;
  onLogin: (password: string) => void;
  onRegister: (password: string, name: string, phone: string) => void;
  phoneNumberInputOptional?: boolean;
  phoneNumberInputHidden?: boolean;
  openForgotPasswordInNewTab?: boolean;
  showPasswordlessLogin?: boolean;
  onRequestPasswordlessLogin?: () => void;
  isRequestingPasswordlessLogin?: boolean;
  hidePasswordField?: boolean;
  onboardingAccountPageCTA?: boolean;
  fullNamePlaceholder?: string;
  fullNameLabel?: string;
  customerDisclaimerText?: string;
  disclaimerProps?: DisclaimerProps;
  passwordFormActionText?: string;
  PhoneInput?: IAccountCombinedFormProps['PhoneInput'];
  NameInput?: IAccountCombinedFormProps['NameInput'];
  PasswordInput?: React.FC<{
    analyticsName: string;
    autoFocus: boolean;
    className: 'od-register-password';
    delay: number;
    errorMessage?: string;
    error?: boolean;
    fontSize: string;
    id: 'password';
    label: 'Password';
    name: 'password';
    onChange: InputProps['onChange'];
    placeholder: string;
    required: boolean;
    type: 'password';
    unboundedWidth: boolean;
    value?: string;
  }>;
}

export interface IAccountPasswordFormState {
  hasError: boolean;
  hasPhoneError: boolean;
  hasNameError: boolean;
  password: string;
  name: string;
  phone: string;
  lastSubmit: {
    name?: string;
    password?: string;
    phone?: string;
  };
}

export default class AccountPasswordForm extends React.Component<
  IAccountPasswordFormProps,
  IAccountPasswordFormState
> {
  constructor(props: IAccountPasswordFormProps) {
    super(props);
    this.state = {
      hasError: false,
      // If the phone number is required, then we have a phone error by default
      hasPhoneError: props.phoneNumberInputOptional ? false : true,
      hasNameError: false,
      password: '',
      name: props.name || '',
      phone: props.phone || '',
      lastSubmit: {},
    };
  }

  public static contextType = NamelessAccountContext;
  context!: React.ContextType<typeof NamelessAccountContext>;

  public static defaultProps = {
    phoneNumberInputOptional: true,
    phoneNumberInputHidden: false,
    PhoneInput: Input,
    NameInput: Input,
    PasswordInput: Input,
  };

  public componentDidMount() {
    this.maybePrefillAccount();
    if (this.context.onPasswordFormLoad) {
      this.context.onPasswordFormLoad();
    }
  }

  public validatePasswordLength = (password: string) => {
    return password.length >= 5 && password.length <= 128;
  };

  public validateName = (name: string) => {
    return name.trim() !== '';
  };

  public onNameChange: InputProps['onChange'] = (_, name) => {
    this.setState({
      hasNameError: isNil(name) || !this.validateName(name),
      name,
    });
  };

  public getPhoneState = (phone?: string) => {
    if (isNil(phone) || phone === '') {
      return { hasPhoneError: !this.props.phoneNumberInputOptional, phone };
    }

    return {
      hasPhoneError: !checkTelValidity(phone),
      phone,
    };
  };

  public onPhoneChange: InputProps['onChange'] = (_event, phone) => {
    this.setState(this.getPhoneState(phone));
  };

  public onPasswordChange: InputProps['onChange'] = (_, password) => {
    this.setState({
      hasError: isNil(password) || !this.validatePasswordLength(password),
      password,
    });
  };

  public handleSubmit = (e: Partial<React.FormEvent<HTMLFormElement>>) => {
    e.preventDefault();
    const { onRegister, onLogin, isNewHuman } = this.props;
    const { password, hasError, hasPhoneError, phone, name, hasNameError, lastSubmit } = this.state;

    if (hasError || (isNewHuman && (hasNameError || hasPhoneError))) {
      return;
    }

    if (
      // Only check to return early if there is previous submission
      Object.values(lastSubmit).filter(Boolean).length !== 0 &&
      name === lastSubmit.name &&
      password === lastSubmit.password &&
      phone === lastSubmit.phone
    ) {
      return;
    }

    if (isNewHuman) {
      if (this.context.shouldRegisterOnEmailSubmit && this.context.onAccountUpdate) {
        this.context.onAccountUpdate(name, phone);
      } else {
        onRegister(password, name, phone);
      }
    } else {
      onLogin(password);
    }

    this.setState({
      lastSubmit: {
        name,
        password,
        phone,
      },
    });
  };

  public render() {
    const {
      analyticsName,
      loading,
      isNewHuman,
      loginError,
      phoneNumberInputOptional,
      phoneNumberInputHidden,
      openForgotPasswordInNewTab,
      showPasswordlessLogin,
      onRequestPasswordlessLogin,
      isRequestingPasswordlessLogin,
      onboardingAccountPageCTA,
      hidePasswordField,
      fullNamePlaceholder,
      fullNameLabel,
      disclaimerProps,
      passwordFormActionText,
      NameInput,
      PasswordInput,
      PhoneInput,
    } = this.props;
    const { password, hasError, name, hasNameError, phone, hasPhoneError } = this.state;

    const shouldDisableSubmit =
      hasError ||
      (!hidePasswordField && (!password || password.length === 0)) ||
      (isNewHuman && (!this.state.name || hasNameError || hasPhoneError));
    const phoneLabelText = phoneNumberInputOptional ? 'Phone (optional)' : 'Phone';

    let actionText = 'Confirm';
    if (phoneNumberInputHidden) {
      actionText = 'Next';
    } else if (onboardingAccountPageCTA) {
      actionText = 'View offer';
    }

    if (passwordFormActionText) {
      actionText = passwordFormActionText;
    }

    const disclaimerText =
      disclaimerProps?.formatText?.({ actionText }) ||
      // Explicitly put 'Confirm' in as action text to maintain prior behavior. In the future, we should change this to {actionText}
      PHONE_DEFAULT_DISCLAIMER('Confirm');

    return (
      <>
        <Form onSubmitCapture={this.handleSubmit}>
          {isNewHuman && (
            <NameInput
              analyticsName={`${analyticsName}-name`}
              autoComplete="name"
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus={true}
              className="od-register-name"
              delay={0}
              errorMessage={hasNameError ? 'Please enter your full name.' : undefined}
              fontSize="s1"
              id="name"
              label={fullNameLabel ? fullNameLabel : 'Full name'}
              name="name"
              onChange={this.onNameChange}
              placeholder={fullNamePlaceholder ? fullNamePlaceholder : 'Jasper Smith'}
              required={true}
              type="text"
              unboundedWidth={true}
              value={name}
            />
          )}
          {!hidePasswordField && (
            <PasswordInput
              analyticsName={`${analyticsName}-password`}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus={!isNewHuman}
              className="od-register-password"
              delay={0}
              errorMessage={
                hasError ? 'Your password must be 5 to 128 characters long.' : undefined
              }
              error={hasError || !!loginError}
              fontSize="s1"
              id="password"
              label="Password"
              name="password"
              onChange={this.onPasswordChange}
              placeholder="Password"
              required={true}
              type="password"
              unboundedWidth={true}
              value={password}
            />
          )}
          {isNewHuman && !phoneNumberInputHidden && (
            <PhoneInput
              analyticsName={`${analyticsName}-phone`}
              autoComplete="tel"
              className="od-register-phone"
              delay={0}
              fontSize="s1"
              id="phone"
              label={phoneLabelText}
              name="phone"
              onChange={this.onPhoneChange}
              required={!phoneNumberInputOptional}
              type="tel"
              unboundedWidth={true}
              value={phone}
            />
          )}
          {loginError && (
            <Box textAlign="left">
              <Text color="red50">
                {typeof loginError === 'string' ? loginError : 'Invalid password.'}
              </Text>
              <u>
                <Link
                  color="red50"
                  _hover={{ color: 'red50' }}
                  _active={{ color: 'red50' }}
                  href="/reset-password"
                  target={openForgotPasswordInNewTab ? '_blank' : '_self'}
                  analyticsName={`${analyticsName}-login-error-reset-password`}
                  aria-label={
                    openForgotPasswordInNewTab
                      ? 'Opens a new window with the reset password page'
                      : 'Opens the reset password page'
                  }
                >
                  Click here to reset your password.
                </Link>
              </u>
            </Box>
          )}
          {disclaimerProps?.location === 'top' && !phoneNumberInputHidden && isNewHuman && (
            <Disclaimer my={5}>{disclaimerText}</Disclaimer>
          )}
          <AccountFormControls
            actionText={actionText}
            analyticsName={analyticsName}
            loading={loading}
            showForgotPassword={!isNewHuman}
            showPasswordlessLogin={showPasswordlessLogin}
            onRequestPasswordlessLogin={onRequestPasswordlessLogin}
            isRequestingPasswordlessLogin={isRequestingPasswordlessLogin}
            disabled={shouldDisableSubmit}
            openForgotPasswordInNewTab={openForgotPasswordInNewTab}
            submitA11yProps={{ 'aria-label': '' }}
          />
        </Form>
        <Disclaimer mt={5}>
          Your information is protected by our&nbsp;
          <Link
            href="https://www.opendoor.com/w/privacy"
            display="inline"
            target="_blank"
            analyticsName={`${analyticsName}-privacy-policy`}
            aria-label="Opens a new page with Opendoor's privacy policy"
          >
            Privacy Policy
          </Link>
          .<br></br>
          <br></br>
          {!phoneNumberInputHidden &&
            isNewHuman &&
            disclaimerProps?.location !== 'top' &&
            disclaimerText}
        </Disclaimer>
      </>
    );
  }

  private maybePrefillAccount = () => {
    const url = new URL(window.location.href);
    const params = new URLSearchParams(url.searchParams);
    const phoneNumber = params.get('phoneNumber') || this.props.phone;
    const { phone, hasPhoneError } = this.getPhoneState(phoneNumber);

    if (params.get('name') || params.get('phoneNumber')) {
      this.setState({
        name: params.get('name') || '',
        phone: hasPhoneError ? '' : phone,
        hasPhoneError,
      });
    }
  };
}
